export const readMapEntry = (map, judgeId) => {
  if (map == null || judgeId === undefined || judgeId === null) return undefined;
  if (Object.prototype.hasOwnProperty.call(map, judgeId)) return map[judgeId];
  const s = String(judgeId);
  if (Object.prototype.hasOwnProperty.call(map, s)) return map[s];
  return undefined;
};

/** Dosya puanlarının toplanıp çıkarılmasından kalan IEEE754 “tozu” (ör. 7e-15) için */
const JUDGE_SCORE_FP_EPSILON = 1e-9;

/**
 * Hakim puanını depolama / gösterim için normalize eder: negatifleri 0 yapar,
 * sıfıra çok yakın pozitif kalıntıları 0 sayar.
 */
export const sanitizeJudgeScore = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  const x = Math.max(0, n);
  return x < JUDGE_SCORE_FP_EPSILON ? 0 : x;
};

/**
 * Kayıtlı bir tevzi dağıtımında bu hakime yansıyan puan katkısı.
 * Semantik: bu tevzinin hakime kattığı TAM puan (dosyaların toplam alınan puanı).
 *
 * Öncelik sırası:
 *  1) Açık `judgeScoreDeltaFromSave` (yeni kayıtlarda tam katkı olarak tutulur).
 *  2) Geriye dönük uyumluluk için `summaryReport.yeniHakimPuanlari − judgeScoresBeforeSave`.
 */
export const getDeltaForJudgeFromDistribution = (distribution, judgeId) => {
  if (!distribution) return 0;

  const explicit = distribution.judgeScoreDeltaFromSave;
  if (explicit && typeof explicit === 'object' && Object.keys(explicit).length > 0) {
    const d = readMapEntry(explicit, judgeId);
    if (d !== undefined && d !== null) {
      const n = Number(d);
      return Number.isFinite(n) ? n : 0;
    }
  }

  const yeniMap = distribution.summaryReport?.yeniHakimPuanlari;
  const beforeMap = distribution.judgeScoresBeforeSave;
  if (
    yeniMap &&
    typeof yeniMap === 'object' &&
    beforeMap &&
    typeof beforeMap === 'object' &&
    Object.keys(beforeMap).length > 0
  ) {
    const y = Number(readMapEntry(yeniMap, judgeId) ?? 0);
    const b = Number(readMapEntry(beforeMap, judgeId) ?? 0);
    return y - b;
  }

  return 0;
};

/**
 * Bu tevzide hakim adına düşen dosya sayısı (özet rapor veya dağıtım listesinden).
 * @returns {Map<string, number>} hakimAdi → dosya adedi
 */
export const getDosyaSayisiByJudgeNameFromDistribution = (distribution) => {
  const map = new Map();
  if (!distribution) return map;

  const rapor = distribution.summaryReport?.hakimRaporlari;
  if (Array.isArray(rapor) && rapor.length > 0) {
    rapor.forEach((h) => {
      if (h?.hakimAdi != null) {
        map.set(h.hakimAdi, Number(h.dosyaSayisi) || 0);
      }
    });
    return map;
  }

  const files = distribution.distributedFiles || [];
  files.forEach((f) => {
    const name = f.atananHakim;
    if (!name) return;
    map.set(name, (map.get(name) || 0) + 1);
  });
  return map;
};

/**
 * Kayıtlı tevzinin hakim dosya sayılarına kattığı net değişim (kayıt sonrası − kayıt öncesi).
 * - Üzerine yazarak tekrar kaydetmede (eski kayıt geri alınıp yenisi uygulanırken) net delta
 *   `yeniSayı − eskiSayı` olur; bu yüzden tercihen açık delta haritası kullanılır.
 * - Eski kayıtlarda açık delta yoksa özet/dağıtım listesinden türetilen hakim adı bazlı sayıya düşer.
 */
export const getDosyaSayisiDeltaForJudgeFromDistribution = (distribution, judge) => {
  if (!distribution || !judge) return 0;

  const explicit = distribution.judgeDosyaSayisiDeltaFromSave;
  if (explicit && typeof explicit === 'object' && Object.keys(explicit).length > 0) {
    const d = readMapEntry(explicit, judge.id);
    if (d !== undefined && d !== null) {
      const n = Number(d);
      return Number.isFinite(n) ? n : 0;
    }
  }

  const dosyaByName = getDosyaSayisiByJudgeNameFromDistribution(distribution);
  return dosyaByName.get(judge.name) ?? 0;
};

export const revertDistributionDeltaFromJudges = (judgesList, distribution) => {
  if (classifyDistributionScoreRevert(distribution) !== 'revert') {
    return judgesList;
  }

  return judgesList.map((j) => {
    const d = getDeltaForJudgeFromDistribution(distribution, j.id);
    const cur = Number(j.score);
    const nextScore = (Number.isFinite(cur) ? cur : 0) - (Number.isFinite(d) ? d : 0);

    const dc = getDosyaSayisiDeltaForJudgeFromDistribution(distribution, j);
    const curCount = Number(j.dosyaSayisi) || 0;
    const nextCount = Math.max(0, curCount - (Number.isFinite(dc) ? dc : 0));

    return { ...j, score: sanitizeJudgeScore(nextScore), dosyaSayisi: nextCount };
  });
};

/**
 * Kayıt puan etkisinin geri alınıp alınamayacağı.
 * - `skip`: Genel hakim listesine uygulanmamış kayıt (puan düşmez).
 * - `revert`: Delta biliniyor; geri alınabilir.
 * - `none`: Puan uygulanmış sayılır ancak delta türetilemiyor; güvenli geri alma yok.
 */
export const classifyDistributionScoreRevert = (distribution) => {
  if (!distribution) return 'none';
  if (distribution.scoresAppliedToJudges === false) return 'skip';

  const explicitDelta = distribution.judgeScoreDeltaFromSave;
  const beforeMap = distribution.judgeScoresBeforeSave;
  const yeniMap = distribution.summaryReport?.yeniHakimPuanlari;

  const hasExplicitDelta =
    explicitDelta && typeof explicitDelta === 'object' && Object.keys(explicitDelta).length > 0;
  const canDeriveDelta =
    yeniMap &&
    typeof yeniMap === 'object' &&
    beforeMap &&
    typeof beforeMap === 'object' &&
    Object.keys(beforeMap).length > 0;

  if (!hasExplicitDelta && !canDeriveDelta) return 'none';
  return 'revert';
};

/**
 * Klasördeki tevzi kayıtlarının puan etkisini, kayıtların uygulanma sırasının *tersiyle* geri alır
 * (önce son kayıt, sonra bir önceki …).
 */
export const revertFolderDistributionsFromJudges = (judgesList, distributions) => {
  if (!Array.isArray(distributions) || distributions.length === 0) return judgesList;
  let current = judgesList;
  for (let i = distributions.length - 1; i >= 0; i -= 1) {
    const d = distributions[i];
    if (!d || classifyDistributionScoreRevert(d) !== 'revert') continue;
    current = revertDistributionDeltaFromJudges(current, d);
  }
  return current;
};
